import { useState } from 'react';

import { type StudentSearch } from '~entities/student';
import { useDeleteStudent } from '~entities/student/api/delete-student';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';

interface DeleteStudentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedStudents: Array<StudentSearch>;
  onClose: () => void;
  isBulkDelete?: boolean;
  onDelete?: () => void;
}

export const DeleteStudentDialog = ({
  open,
  onOpenChange,
  selectedStudents = [],
  onClose,
  isBulkDelete = false,
  onDelete,
}: DeleteStudentDialogProps) => {
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteMutation = useDeleteStudent();

  const handleClose = () => {
    setDeleteConfirmText('');
    onClose();
  };

  // Check if any selected student is active
  const hasActiveStudents = selectedStudents.some((student) => student.active);

  // Get details of active students
  const activeStudents = selectedStudents.filter((student) => student.active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Delete {isBulkDelete ? 'Students' : 'Student'}
          </DialogTitle>
        </DialogHeader>

        {!hasActiveStudents ? (
          <>
            <DialogDescription>
              {isBulkDelete
                ? 'Are you sure you want to delete these students? This action cannot be undone.'
                : 'Are you sure you want to delete this student? This action cannot be undone.'}
            </DialogDescription>
            <div className='mt-4'>
              <Input
                value={deleteConfirmText}
                onChange={(e) => {
                  setDeleteConfirmText(e.target.value);
                }}
                onPaste={(e) => {
                  e.preventDefault();
                }}
                placeholder="Type 'delete' to confirm"
              />
            </div>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant='destructive'
                disabled={deleteConfirmText !== 'delete'}
                onClick={() => {
                  if (isBulkDelete && onDelete) {
                    onDelete();
                  } else {
                    deleteMutation.mutate({
                      studentId: selectedStudents[0]?._id ?? '',
                    });
                  }
                  handleClose();
                }}
              >
                {isBulkDelete ? 'Delete Students' : 'Delete Student'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogDescription className='text-destructive'>
              {isBulkDelete
                ? 'The following students have active enrollments and cannot be deleted:'
                : 'This student has active enrollments and cannot be deleted:'}
              <ul className='mt-2 list-disc pl-4'>
                {activeStudents.map((student) => (
                  <li key={student.name}>
                    <span className='font-medium text-black'>
                      {student.name}
                    </span>{' '}
                    has active enrollments
                  </li>
                ))}
              </ul>
              Please remove all enrollments before deleting{' '}
              {isBulkDelete ? 'these students' : 'this student'}.
            </DialogDescription>
            <DialogFooter>
              <Button variant='outline' onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
